import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FormLayout,
  TextField,
  Form,
  Button,
  Banner,
  Text,
  Select,
} from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DatePickerx from "features/datepicker/date-picker";
import { getAllCountryOptions, getCountryName } from "constants/countries";
import {
  createBusinessTrip,
  fetchBusinessTripById,
  updateBusinessTrip,
} from "services/business-trip.service";
import { fetchBusinessTripsThunk } from "containers/business-trips/business-trips.slice";
import { fetchUserBusinessTripsThunk } from "containers/user-business-trips/user-business-trips.slice";
import { AppDispatch, RootState } from "configureStore";
import { setIsModalxOpen } from "components/modalx/modalx.slice";
import UserSelect from "features/user-select/user-select";
import { fetchBusinessTripDetails } from "containers/business-trip-view-container/business-trip-container.slice";
import { setTypedDecimal } from "utils/decimal-input";

interface BusinessTripFormProps {
  id?: string;
  source?: string;
}

const BusinessTripForm: React.FC<BusinessTripFormProps> = ({
  id,
  source = "",
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const { role } = useSelector((state: RootState) => state.authReducer);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [transportUsed, setTransportUsed] = useState("");
  const [purpose, setPurpose] = useState("");
  const [advanceMoney, setAdvanceMoney] = useState("");
  const [signatureUser, setSignatureUser] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  const [errMsg, setErrMsg] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const isEditMode = !!id;

  useEffect(() => {
    if (isEditMode && companyId && id) {
      (async () => {
        try {
          const data = await fetchBusinessTripById(id, companyId);
          setDateFrom(
            data.dateFrom
              ? (typeof data.dateFrom === "string"
                  ? data.dateFrom
                  : new Date(data.dateFrom).toISOString().split("T")[0]
                ).slice(0, 10)
              : ""
          );
          setDateTo(
            data.dateTo
              ? (typeof data.dateTo === "string"
                  ? data.dateTo
                  : new Date(data.dateTo).toISOString().split("T")[0]
                ).slice(0, 10)
              : ""
          );
          setCountry(data.country || "");
          setCity(data.city || "");
          setTransportUsed(data.transportUsed || "");
          setPurpose(data.purpose || "");
          setAdvanceMoney(
            data.advanceMoney != null ? String(data.advanceMoney) : ""
          );
          setSignatureUser(
            typeof data.signatureUser === "object" && data.signatureUser?.id
              ? data.signatureUser.id
              : typeof data.signatureUser === "string"
                ? data.signatureUser
                : ""
          );
        } catch (e) {
          setErrMsg("error");
        }
      })();
    }
  }, [isEditMode, companyId, id]);

  const handleFormSubmit = async () => {
    const newErrors: Record<string, string> = {};
    if (!dateFrom) newErrors.dateFrom = t("field_is_required");
    if (!dateTo) newErrors.dateTo = t("field_is_required");
    if (dateFrom && dateTo && new Date(dateFrom) > new Date(dateTo)) {
      newErrors.dateTo = t("date_to_must_be_after_date_from");
    }
    if (!country?.trim()) newErrors.country = t("field_is_required");
    if (!city?.trim()) newErrors.city = t("field_is_required");
    if (!transportUsed?.trim()) newErrors.transportUsed = t("field_is_required");
    if (!purpose?.trim()) newErrors.purpose = t("field_is_required");
    // The field accepts a lone separator while a number is being typed; it must not pass as a number.
    if (advanceMoney && isNaN(Number(advanceMoney))) {
      newErrors.advanceMoney = t("must_be_number");
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const placeOfBusinessTrip =
      country && city ? `${getCountryName(country, t)}, ${city}` : undefined;

    const payload: any = {
      dateFrom,
      dateTo,
      country: country || undefined,
      city: city || undefined,
      placeOfBusinessTrip,
      transportUsed: transportUsed || undefined,
      purpose: purpose || undefined,
      advanceMoney:
        advanceMoney === "" ? undefined : parseFloat(advanceMoney) || undefined,
      signatureUser: signatureUser || null,
    };
    if (source === "profile" && role?.user) {
      payload.user = role.user;
    }

    try {
      setIsLoading(true);
      if (isEditMode && companyId && id) {
        const result = await updateBusinessTrip(companyId, id, payload);
        dispatch(fetchBusinessTripDetails({ companyId, tripId: id }));
        setSuccessMessage(result?.documentInvalidated ? "trip_updated_document_invalidated" : "ok");
      } else if (companyId) {
        await createBusinessTrip(companyId, payload);
        setSuccessMessage("ok");
      }
      dispatch(fetchBusinessTripsThunk(companyId!, {}));
      dispatch(fetchUserBusinessTripsThunk(companyId!, {}));
      setTimeout(() => {
        dispatch(setIsModalxOpen(false));
      }, 1500);
    } catch (e) {
      setErrMsg("error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      {(errMsg || successMessage) && (
        <div style={{ marginBottom: "16px" }}>
          {errMsg && <Banner tone="critical">{t(errMsg)}</Banner>}
          {successMessage && (
            <Banner tone="success">{t(successMessage)}</Banner>
          )}
        </div>
      )}
      <FormLayout>
        {/* Each date picker is at least 275px wide: side by side only where there is room. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <DatePickerx
              title={t("date_from")}
              selected={dateFrom}
              onChange={(value) =>
                setDateFrom(
                  value ? value.toISOString().split("T")[0] : ""
                )
              }
              isRequired={true}
              disableDatesAfter={null}
            />
          </div>
          <div>
            <DatePickerx
              title={t("date_to")}
              selected={dateTo}
              onChange={(value) =>
                setDateTo(value ? value.toISOString().split("T")[0] : "")
              }
              isRequired={true}
              disableDatesAfter={null}
            />
          </div>
        </div>
        {errors.dateFrom && (
          <Text as="p" variant="bodySm" tone="critical">
            {errors.dateFrom}
          </Text>
        )}
        {errors.dateTo && (
          <Text as="p" variant="bodySm" tone="critical">
            {errors.dateTo}
          </Text>
        )}
        <Select
          label={t("country")}
          options={[
            {
              value: "",
              label: t("files_template_custom_type_select_select_an_option"),
            },
            ...getAllCountryOptions(t),
          ]}
          value={country}
          onChange={setCountry}
          requiredIndicator
          error={errors.country}
        />
        <TextField
          label={t("city")}
          value={city}
          onChange={setCity}
          autoComplete="off"
          requiredIndicator
          error={errors.city}
        />
        <TextField
          label={t("transport_used")}
          value={transportUsed}
          onChange={setTransportUsed}
          autoComplete="off"
          requiredIndicator
          error={errors.transportUsed}
        />
        <TextField
          label={t("purpose")}
          value={purpose}
          onChange={setPurpose}
          multiline={3}
          autoComplete="off"
          requiredIndicator
          error={errors.purpose}
        />
        <TextField
          label={t("advance_for_expenses")}
          value={advanceMoney}
          inputMode="decimal"
          onChange={(typed) => setTypedDecimal(setAdvanceMoney, typed)}
          autoComplete="off"
          error={errors.advanceMoney}
        />
        <UserSelect
          label={t("substitute_person")}
          value={signatureUser}
          onChange={(v: string) => setSignatureUser(v)}
        />
        <Button submit loading={isLoading}>
          {t(isEditMode ? "update" : "save")}
        </Button>
      </FormLayout>
    </Form>
  );
};

export default BusinessTripForm;
